import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { CaretRightOutlined, FileTextOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { wikiApi, type WikiArticleListItem } from '../lib/wikiApi';
import { authApi } from '../lib/authApi';
import { makeSectionComparator } from '../lib/sectionOrder';
import { NewArticleModal } from './NewArticleModal';

/**
 * Левое дерево «Страницы» раздела (макет кита FISHKA HUB). Раньше показывало
 * плоский parent-список (все статьи блока вперемешку, по алфавиту с эмодзи) —
 * юзер просил как в ките: группировка по ПОД-РАЗДЕЛАМ (категориям) со
 * счётчиками и сворачиванием (Подготовка · Помещение · Ремонт · …).
 *
 * Данные — те же, что у BlockPage (listArticles → categoryName), группируем
 * по категории. Активная статья подсвечивается по slug из роутера; её группа
 * раскрыта. Порядок групп — по минимальному sortOrder статей внутри (следует
 * за drag-порядком карточек на странице раздела).
 */
const ROOT = '__root__';

export function PageTree({ blockCode }: { blockCode: string; editMode?: boolean }): JSX.Element | null {
  const params = useParams<{ slug?: string }>();
  const qc = useQueryClient();
  // Предзагрузка статьи в кэш React Query — клик/переход становится мгновенным
  // (без белого экрана и ожидания сети). Тот же ключ/staleTime что в ArticlePage.
  const prefetchArticle = (slug: string): void => {
    void qc.prefetchQuery({
      queryKey: ['wiki', 'article', slug],
      queryFn: () => wikiApi.getArticle(slug),
      staleTime: 60_000,
    });
  };

  const articlesQuery = useQuery({
    queryKey: ['wiki', 'articles', { blockCode, sort: 'order' }],
    queryFn: () => wikiApi.listArticles({ blockCode, limit: 200, sort: 'order' }),
    enabled: !!blockCode,
    staleTime: 30_000,
  });
  // Категории с sortOrder — для порядка групп по ручному (drag) порядку.
  const categoriesQuery = useQuery({
    queryKey: ['wiki', 'categories', blockCode],
    queryFn: () => wikiApi.listCategories(blockCode),
    enabled: !!blockCode,
    staleTime: 30_000,
  });
  const sectionCmp = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of categoriesQuery.data ?? []) {
      const prev = m.get(c.name);
      if (prev == null || c.sortOrder < prev) m.set(c.name, c.sortOrder);
    }
    return makeSectionComparator(m);
  }, [categoriesQuery.data]);
  const meQuery = useQuery({
    queryKey: ['wiki-auth', 'me'],
    queryFn: () => authApi.me(),
    retry: false,
    staleTime: 60_000,
  });
  const canCreate = meQuery.data?.canCreate ?? false;

  // Группируем статьи блока по категории. Группа без категории → ROOT (вверху).
  const groups = useMemo(() => {
    const items = articlesQuery.data?.items ?? [];
    const map = new Map<string, WikiArticleListItem[]>();
    for (const a of items) {
      const key = a.categoryName ?? ROOT;
      const arr = map.get(key) ?? [];
      arr.push(a);
      map.set(key, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.sortOrder - b.sortOrder);
    // Порядок групп: ROOT первым, остальные — по номеру в названии раздела
    // (как на странице блока — единый порядок, см. compareSectionNames).
    return [...map.entries()].sort((x, y) => {
      if (x[0] === ROOT) return -1;
      if (y[0] === ROOT) return 1;
      return sectionCmp(x[0], y[0]);
    });
  }, [articlesQuery.data, sectionCmp]);

  // По умолчанию (как в Яндекс.Облаке) показываем только заголовки разделов —
  // всё свёрнуто, страницы раскрываются по клику. Раздел текущей статьи не
  // разворачиваем автоматически, лишь подсвечиваем его заголовок. ROOT
  // (безымянная группа) всегда открыта — у неё нет заголовка-переключателя.
  const activeCat = useMemo(() => {
    for (const [cat, items] of groups) {
      if (items.some((a) => a.slug === params.slug)) return cat;
    }
    return null;
  }, [groups, params.slug]);
  // Предзагрузка всех соседей активного раздела в фоне — переход внутри раздела
  // становится мгновенным (по просьбе: «чтобы сразу весь раздел подгружался»).
  useEffect(() => {
    if (!activeCat) return;
    const items = groups.find(([cat]) => cat === activeCat)?.[1] ?? [];
    for (const a of items) {
      if (a.slug !== params.slug) prefetchArticle(a.slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCat, groups]);

  // overrides: ручные раскрытия/сворачивания поверх дефолта. По умолчанию
  // раскрыта группа активной статьи (activeCat) — при первом переходе она
  // должна быть открыта; остальные свёрнуты. Ручной toggle перекрывает дефолт.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const isOpen = (key: string): boolean =>
    key === ROOT ? true : (overrides[key] ?? key === activeCat);
  const toggle = (key: string): void =>
    setOverrides((c) => ({ ...c, [key]: !isOpen(key) }));

  if (articlesQuery.isLoading) {
    return <div style={{ padding: 12, color: 'var(--fk-text-muted)', fontSize: 12 }}>загрузка…</div>;
  }
  if (articlesQuery.error || !articlesQuery.data) return null;
  if (groups.length === 0) {
    return (
      <div style={{ padding: '12px 16px' }}>
        <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginBottom: 8 }}>
          В этом разделе ещё нет страниц
        </div>
        {canCreate && <CreateButton blockCode={blockCode} />}
      </div>
    );
  }

  return (
    <div style={{ padding: '4px 0' }}>
      {groups.map(([cat, items], gi) => {
        const isRoot = cat === ROOT;
        const open = isOpen(cat);
        // Авто-нумерация подразделов по позиции (как на странице раздела):
        // цифру в названии не пишем — номер = позиция. Существующую ведущую
        // «N.» в имени убираем. ROOT (если есть) всегда первый — смещаем.
        const hasRoot = groups.some(([c]) => c === ROOT);
        const catLabel = isRoot
          ? cat
          : `${gi - (hasRoot ? 1 : 0) + 1}. ${cat.replace(/^\s*\d+\.\s*/, '')}`;
        return (
          <div key={cat} style={{ marginBottom: 2 }}>
            {/* Заголовок категории (кроме безымянной ROOT-группы) */}
            {!isRoot && (
              <button
                type="button"
                onClick={() => toggle(cat)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                  padding: '7px 12px', border: 'none', background: 'transparent',
                  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                  color: cat === activeCat ? 'var(--fk-orange)' : 'var(--fk-text)',
                  fontWeight: 700, fontSize: 13,
                }}
              >
                <CaretRightOutlined
                  style={{ fontSize: 10, color: 'var(--fk-text-muted)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .1s', flexShrink: 0 }}
                />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{catLabel}</span>
                <span style={{ fontSize: 11, color: 'var(--fk-text-muted)', fontWeight: 700 }}>{items.length}</span>
              </button>
            )}
            {/* Статьи категории */}
            {open && items.map((a) => {
              const active = params.slug === a.slug;
              return (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  onMouseEnter={() => prefetchArticle(a.slug)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 12px 6px',
                    paddingLeft: isRoot ? 14 : 30,
                    borderLeft: active ? '3px solid var(--fk-orange)' : '3px solid transparent',
                    background: active ? 'var(--fk-orange-soft)' : 'transparent',
                    color: active ? 'var(--fk-orange)' : 'var(--fk-text-2)',
                    fontWeight: active ? 600 : 400, fontSize: 13, textDecoration: 'none',
                    borderRadius: '0 6px 6px 0', lineHeight: 1.35,
                  }}
                  className="page-tree-link"
                >
                  <FileTextOutlined style={{ fontSize: 12, color: active ? 'var(--fk-orange)' : 'var(--fk-text-muted)', flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</span>
                </Link>
              );
            })}
          </div>
        );
      })}
      {canCreate && (
        <div style={{ padding: '8px 12px 2px' }}>
          <CreateButton blockCode={blockCode} />
        </div>
      )}
      <style>{`.page-tree-link:hover { background: var(--fk-bg-subtle) !important; }`}</style>
    </div>
  );
}

function CreateButton({ blockCode }: { blockCode: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          background: 'transparent', border: '1px dashed var(--fk-border-strong)',
          borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 12,
          color: 'var(--fk-text-muted)', width: '100%', display: 'flex',
          alignItems: 'center', gap: 6, fontFamily: 'inherit',
        }}
      >
        <PlusOutlined /> Создать страницу
      </button>
      <NewArticleModal open={open} onClose={() => setOpen(false)} blockCode={blockCode} />
    </>
  );
}
